/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-undef */
import React, { useEffect, useState } from "react";

import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import styled from "styled-components";
import useHomeFilters from "../../../store/hooks/useHomeFilters";

import { Map, useMap } from "react-mapkit";
import "./style.css";
import { RedIconSVG, BlueIconSVG, JobIconSVG, JobIconOrange } from "../../../utils/Images";
import { useCallback } from "react";
import { formatNumberWithCommas } from "../../../utils/helperFunctions";

const DEFAULT_LATITUDE_SPAN = 0.2;
const DEFAULT_LONGITUDE_SPAN = 0.2;
const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

const roleTypes = {
  SELLER: "seller",
  BUYER: "buyer",
  EMPLOYER: "employer"
};

const getDesc = (description = "") => {
  const limit = 34;
  return description.length <= limit
    ? description
    : `${description.slice(0, limit)}...`;
};

function createCard(
  type,
  username,
  description,
  price,
  rating,
  profilePic,
  onPressDescription = () => {},
  onPressProfileName = () => {}
) {
  const card = document.createElement("div");
  card.className = "card";
  const header = document.createElement("div");
  header.className = "header";

  const profileImg = document.createElement("img");
  profileImg.src = profilePic;
  profileImg.className = "profile-img";

  const usernameElement = document.createElement("p");
  usernameElement.className = "username";
  usernameElement.innerText = username;
  usernameElement.onclick = onPressProfileName;
  header.appendChild(usernameElement);
  header.appendChild(profileImg);

  const ratingContainer = document.createElement("div");
  ratingContainer.className = "rating-container";
  const currentRating = rating;
  const ratingValueElement = document.createElement("span");
  if (currentRating > 0) {
    const ratingTextElement = document.createElement("span");
    ratingTextElement.innerText = "Rating: ";
    ratingContainer.appendChild(ratingTextElement);
    ratingValueElement.className = "rating-value";
  }
  ratingValueElement.innerText =
    currentRating > 0
      ? currentRating
      : type === roleTypes.BUYER
      ? "New Buyer"
      : "Rookie";
  ratingContainer.appendChild(ratingValueElement);
  const content = document.createElement("div");
  content.className = "card-content";
  content.textContent = getDesc(description);
  content.onclick = onPressDescription;

  const footer = document.createElement("div");
  footer.className = "footer-content d-flex justify-content-between";
  const priceContainer = document.createElement("div");
  const priceTextElement = document.createElement("span");
  priceTextElement.innerText = "Price:";
  const priceValueElement = document.createElement("span");
  priceValueElement.innerText = price;
  priceValueElement.className = "price";
  priceContainer.appendChild(priceTextElement);
  priceContainer.appendChild(priceValueElement);
  footer.appendChild(priceContainer);
  footer.appendChild(ratingContainer);
  card.appendChild(header);
  card.appendChild(content);
  card.appendChild(footer);

  return card;
}

const calloutElementForAnnotation = (annotation) => {
  const data = annotation.data;
  const card = createCard(
    data.type,
    data.username,
    data.description,
    `$${data.priceFrom}-$${data.priceTo}`,
    data.rating,
    data.profilePic,
    data.onPressDescription,
    data.onAvatarClick
  );
  const div = document.createElement("div");
  div.appendChild(card);
  return div;
};

const HomeAppleMapView = ({
  filterGigs,
  filterJobs,
  filterProjects,
  gigs,
  jobs,
  projects,
  gigsLoading,
  jobsLoading,
  projectLoading,
  navigateTo,     // { lat, lng } set by AI search — triggers animated pan
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [rerender, setRerender] = useState(0);
  const { map, mapProps, mapkit, setRegion } = useMap({
    showsUserLocation: true,
    showsUserLocationControl: true,
  });

  // Read AI search text directly from Redux — avoids the React child-before-parent
  // effect timing issue where URL params aren't yet set when the map bbox fetch fires.
  const aiSearchText = useSelector((state) => state.aiSearch?.filters?.searchText || "");

  const { filters } = useHomeFilters();

  const setLocation = (
    lat,
    lng,
    latitudeSpan = DEFAULT_LATITUDE_SPAN,
    longitudeSpan = DEFAULT_LONGITUDE_SPAN
  ) => {
    setRegion(
      {
        latitude: lat,
        longitude: lng,
        latitudeSpan: latitudeSpan,
        longitudeSpan: longitudeSpan,
      },
      true
    );
  };

  useEffect(() => {
    setLocation(
      filters.geometryFilter.data.lat,
      filters.geometryFilter.data.lng
    );
  }, [filters.geometryFilter, map]);

  // Pan map to AI-searched location when navigateTo prop changes
  useEffect(() => {
    if (!map || !navigateTo?.lat || !navigateTo?.lng) return;
    setLocation(
      navigateTo.lat,
      navigateTo.lng,
      navigateTo.latSpan || 0.3,
      navigateTo.lngSpan || 0.3
    );
  }, [map, navigateTo]);

  const onMapPointChange = useCallback(() => {
    const visibleMapRect = map.visibleMapRect;
    const { size, origin } = visibleMapRect;
    const halfHeight = size.height / 2;
    const halfWidth = size.width / 2;

    const northEastRect = new mapkit.MapRect(
      origin.x - halfWidth,
      origin.y - halfHeight,
      size.width,
      size.height
    );
    const southWestRect = new mapkit.MapRect(
      origin.x + halfWidth,
      origin.y + halfHeight,
      size.width,
      size.height
    );
    const northEastCoordinates = northEastRect.toCoordinateRegion();
    const southWestCoordinates = southWestRect.toCoordinateRegion();

    const neLat = northEastCoordinates.center.latitude;
    const neLng = northEastCoordinates.center.longitude;
    const swLat = southWestCoordinates.center.latitude;
    const swLng = southWestCoordinates.center.longitude;

    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("neLat", neLat);
      newParams.set("neLng", neLng);
      newParams.set("swLat", swLat);
      newParams.set("swLng", swLng);
      return newParams;
    });
  }, [map, mapkit]);

  useEffect(() => {
    const neLat = searchParams.get("neLat");
    const neLng = searchParams.get("neLng");
    const swLat = searchParams.get("swLat");
    const swLng = searchParams.get("swLng");
    if (!neLat || !neLng || !swLat || !swLng) {
      return;
    }
    const page = 1;
    const limit = 200;
    const params = {
      neLat,
      neLng,
      swLat,
      swLng,
      page,
      limit,
    };
    const promises = [];
    
    // Get common search text — prefer Redux AI state (always up-to-date) over
    // URL params (which may not be set yet due to React effect ordering).
    const commonSearchText = aiSearchText || searchParams.get("query") || "";
    
    const gigUrlFilters = {
      searchText: searchParams.get("gigs.searchText") || commonSearchText,
      sortBy: searchParams.get("gigs.sortBy") || "",
      level: searchParams.get("gigs.level") || "",
      minPrice: searchParams.get("gigs.minPrice") || "",
      maxPrice: searchParams.get("gigs.maxPrice") || "",
    };
    const jobUrlFilters = {
      searchText: searchParams.get("jobs.searchText") || commonSearchText,
      sortBy: searchParams.get("jobs.sortBy") || "",
      workType: searchParams.get("jobs.workType") || "",
      minPrice: searchParams.get("jobs.min-budgetLimit") || "",
      maxPrice: searchParams.get("jobs.max-budgetLimit") || "",
    };
    if (filters.sectionFilter.includes("gigs") || filters.sectionFilter === "all") {
      promises.push(filterGigs({ ...params, ...gigUrlFilters }));
    }
    if (filters.sectionFilter.includes("jobs") || filters.sectionFilter === "all") {
      promises.push(filterJobs({ ...params, ...jobUrlFilters }));
    }
    if (filters.sectionFilter.includes("projects") || filters.sectionFilter === "all") {
      promises.push(filterProjects({ ...params, ...jobUrlFilters }));
    }
    
    Promise.all(promises).catch((error) => {
      console.error("Error fetching map data:", error);
    });
  }, [searchParams, filterGigs, filterJobs, filterProjects, filters.sectionFilter, aiSearchText]);

  useEffect(() => {
    if (map) {
      map.addEventListener("region-change-end", onMapPointChange);
      map.removeAnnotations(map.annotations);
      const markers = [];
      if (
        gigs &&
        gigs.length > 0 &&
        (filters.sectionFilter.includes("gigs") || filters.sectionFilter == "all")
      ) {
        gigs.forEach((gig) => {
          const rating = gig.rating;
          var pinOptions = {
            url: { 1: BlueIconSVG },
            callout: {
              calloutElementForAnnotation,
            },
            data: {
              id: gig._id,
              type: roleTypes.SELLER,
              userId: gig.createdBy._id,
              username: gig.createdBy.username,
              profilePic: gig.createdBy.profileImg?.imgUrl,
              description: gig.title,
              priceFrom: formatNumberWithCommas(gig.priceFrom),
              priceTo: formatNumberWithCommas(gig.priceTo),
              rating,
              onPressDescription: () => {
                window.open(`/gig/${gig._id}/detail`, "_blank");
              },
              onAvatarClick: () => {
                window.open(
                  `/profile/${gig.createdBy._id}?mode=seller`,
                  "_blank"
                );
              },
            },
          };

          var coordinate = new mapkit.Coordinate(gig.latitude, gig.longitude);
          const factory = function (coordinate, options) {
            const img = document.createElement("img");
            img.style = "cursor:pointer";
            img.src = BlueIconSVG;
            return img;
          };
          const annotation = new mapkit.Annotation(
            coordinate,
            factory,
            pinOptions
          );
          annotation.calloutOffset = new DOMPoint(0, 3);
          markers.push(annotation);
        });
      }
      if (
        jobs &&
        jobs.length > 0 &&
        (filters.sectionFilter.includes("jobs")|| filters.sectionFilter == "all")
      ) {
        jobs.forEach((job) => {
          const rating = job.createdBy.rating;
          var pinOptions = {
            url: { 1: JobIconOrange },
            callout: {
              calloutElementForAnnotation,
            },
            data: {
              id: job._id,
              type: roleTypes.EMPLOYER,
              description: job.description,
              userId: job.createdBy._id,
              username: job.createdBy.username,
              profilePic: job.createdBy.profileImg?.imgUrl,
              priceFrom: formatNumberWithCommas(job.budgetMin),
              priceTo: formatNumberWithCommas(job.budgetMax),
              rating,
              onPressDescription: () => {
                if (job.is_external) {
                  const redirectUrl = `${API_BASE}/v1/external-jobs/redirect/${job._id}?source=web-apple-map`;
                  window.open(redirectUrl || job.external_apply_url, "_blank", "noopener,noreferrer");
                } else {
                  window.open(`/jobs/${job._id}/detail`, "_blank");
                }
              },
              onAvatarClick: () => {
                if(job.is_external) {
                  const redirectUrl = `${API_BASE}/v1/external-jobs/redirect/${job._id}?source=web-apple-map`;
                  window.open(redirectUrl || job.external_apply_url, "_blank", "noopener,noreferrer");
                } else {
                  window.open(
                    `/profile/${job.createdBy._id}?mode=buyer`,
                    "_blank"
                  );
                }
              },
            },
          };
          var coordinate = new mapkit.Coordinate(
            job.location.latitude,
            job.location.longitude
          );
          const factory = function (coordinate, options) {
            const img = document.createElement("img");
            img.style = "cursor:pointer; width:40px; height:40px";
            img.src = JobIconOrange;
            return img;
          };
          const annotation = new mapkit.Annotation(
            coordinate,
            factory,
            pinOptions,
          );
          annotation.calloutOffset = new DOMPoint(0, 7);

          markers.push(annotation);
        });
      }
      if (
        projects &&
        projects.length > 0 &&
        (filters.sectionFilter.includes("projects") || filters.sectionFilter == "all")
      ){
        projects.forEach((project) => {
          const rating = project.createdBy.rating;
          var pinOptions = {
            url: { 1: RedIconSVG },
            callout: {
              calloutElementForAnnotation,
            },
            data: {
              id: project._id,
              type: roleTypes.BUYER,
              description: project.description,
              userId: project.createdBy._id,
              username: project.createdBy.username,
              profilePic: project.createdBy.profileImg?.imgUrl,
              priceFrom: project.budgetMin,
              priceTo: project.budgetMax,
              rating,
              onPressDescription: () => {
                window.open(`/projects/${project._id}/detail`, "_blank");
              },
              onAvatarClick: () => {
                window.open(
                  `/profile/${project.createdBy._id}`,
                  "_blank"
                );
              },
            },
          };
          var coordinate = new mapkit.Coordinate(
            project.location.latitude,
            project.location.longitude,
          );
          const factory = function (coordinate, options) {
            const img = document.createElement("img");
            img.style = "cursor:pointer";
            img.src = RedIconSVG;
            return img;
          };
          const annotation = new mapkit.Annotation(
            coordinate,
            factory,
            pinOptions
          );
          annotation.calloutOffset = new DOMPoint(0, 7);
          markers.push(annotation);
        });
      }
      map.addAnnotations(markers);

      return () => {
        map.removeAnnotations(map.annotations);
        map.removeOverlays(map.overlays);
        map.removeEventListener("region-change-end", onMapPointChange);
      };
    }
  }, [map, gigs, jobs, filters.sectionFilter, rerender]);

  const handleZoomIn = () => {
    const newRegion = new mapkit.CoordinateRegion(
      map.region.center,
      new mapkit.CoordinateSpan(
        map.region.span.latitudeDelta * 0.5,
        map.region.span.longitudeDelta * 0.5
      )
    );
    map.setRegionAnimated(newRegion);
  };

  const handleZoomOut = () => {
    const newRegion = new mapkit.CoordinateRegion(
      map.region.center,
      new mapkit.CoordinateSpan(
        map.region.span.latitudeDelta * 2,
        map.region.span.longitudeDelta * 2
      )
    );
    map.setRegionAnimated(newRegion);
  };

  return (
    <StyledMapView>
      <div className="map position-relative">
        <Map {...mapProps} />
        <div style={{ position: "absolute", top: 50, right: 50 }}>
          <ZoomButton onClick={handleZoomIn}>+</ZoomButton>
          <ZoomButton onClick={handleZoomOut}>-</ZoomButton>
          {(gigsLoading || jobsLoading || projectLoading) ? <MapLoadingButton /> : null}
        </div>
      </div>
    </StyledMapView>
  );
};

/**
 * @typedef {Object} Coordinates
 * @property {Object} center
 * @property {number} center.lat
 * @property {number} center.lng
 * @property {number} zoom
 *
 *
 * @typedef Props
 * @property {Coordinates} coordinates
 * @property {(newCoordinate:Coordinates) => void} setCoordinates
 */

const ZoomButton = styled.div`
  display: block;
  background-color: rgb(245, 245, 245, 0.9);
  color: rgb(112, 115, 112);
  width: 24px;
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: rgba(0, 0, 0, 0.16) 0px 1px 4px;
  padding: 8px 8px;
  font-size: 22px;
  text-align: center;
  cursor: pointer;
`;

const MapLoadingButton = styled.div`
  margin-top: 16px;
  width: 24px;
  aspect-ratio: 1;
  border-radius: 50%;

  border: 8px solid #ddf9f5;
  border-right-color: #0fd3bb;
  animation: l2 1s infinite linear;
  @keyframes l2 {
    to {
      transform: rotate(1turn);
    }
  }
`;

const StyledMapView = styled.div`
  .map {
    display: block;
    height: 80vh;
    width: 100vw;
  }
  .map-zoom-buttons {
    position: absolute;
    top: 50;
    right: 7;
  }
  .map-zoom-button {
    width: 4px;
    height: 4px;
    background-color: white;
    border-radius: 10px;
  }
  @media only screen and (min-width: 769px) {
    .map {
      display: block;
      height: 80vh;
      width: 50vw;
    }
  }
`;

export default HomeAppleMapView;
